import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useUserData } from '@/hooks/useUserData'
import BoardPage from '@/pages/BoardPage'
import GuestArtistsPage from '@/pages/GuestArtistsPage'
import DiscoverPage from '@/pages/DiscoverPage'
import ArtistMatchPage from '@/pages/ArtistMatchPage'
import ProfilePage from '@/pages/ProfilePage'
import ChatsPage from '@/pages/ChatsPage'
import {
  persistentMainPaneForPath,
  type PersistentMainPaneId,
} from '@/lib/mainAppPersistentLayer'

function paneClass(active: boolean) {
  return active ? 'block' : 'hidden'
}

function initialMountedPanes(pathname: string): Set<PersistentMainPaneId> {
  const pane = persistentMainPaneForPath(pathname)
  return pane ? new Set([pane]) : new Set()
}

/**
 * Keeps bottom-nav destinations mounted so in-memory state survives tab switches.
 * Panes mount on first visit, then stay mounted. Detail routes stay on React Router.
 */
export function MainAppPersistentLayer() {
  const location = useLocation()
  const { isArtist, loading: userDataLoading, getCachedProfile } = useUserData()

  const cachedProfile = getCachedProfile()
  const effectiveIsArtist = userDataLoading
    ? (cachedProfile?.isArtist ?? isArtist)
    : isArtist

  const pathname = location.pathname
  const activePane = persistentMainPaneForPath(pathname)

  const [mountedPanes, setMountedPanes] = useState<Set<PersistentMainPaneId>>(() =>
    initialMountedPanes(pathname),
  )

  useEffect(() => {
    if (!activePane) return
    setMountedPanes((current) => {
      if (current.has(activePane)) return current
      const next = new Set(current)
      next.add(activePane)
      return next
    })
  }, [activePane])

  const showBoard = activePane === 'board'
  const showGuests = activePane === 'guests'
  const showDiscover = activePane === 'discover'
  const showMatcher = activePane === 'matcher'
  const showProfile = activePane === 'profile'
  const showChatsList = activePane === 'chats'

  const mountMatcherPane = !effectiveIsArtist || mountedPanes.has('matcher')
  const mountProfilePane = !effectiveIsArtist && mountedPanes.has('profile')

  return (
    <div className="main-app-persistent-layer">
      {mountedPanes.has('board') ? (
        <div className={paneClass(showBoard)} aria-hidden={!showBoard}>
          <BoardPage isActive={showBoard} />
        </div>
      ) : null}
      {mountedPanes.has('guests') ? (
        <div className={paneClass(showGuests)} aria-hidden={!showGuests}>
          <GuestArtistsPage />
        </div>
      ) : null}
      {mountedPanes.has('discover') ? (
        <div className={paneClass(showDiscover)} aria-hidden={!showDiscover}>
          <DiscoverPage />
        </div>
      ) : null}
      {mountMatcherPane ? (
        <div className={paneClass(showMatcher)} aria-hidden={!showMatcher}>
          <ArtistMatchPage />
        </div>
      ) : null}
      {mountProfilePane ? (
        <div className={paneClass(showProfile)} aria-hidden={!showProfile}>
          <ProfilePage />
        </div>
      ) : null}
      {mountedPanes.has('chats') ? (
        <div className={paneClass(showChatsList)} aria-hidden={!showChatsList}>
          <ChatsPage />
        </div>
      ) : null}
    </div>
  )
}
